// =======================================================
// Engine.js — core renderer, scene, post-processing, loop
// =======================================================
//
// Rendering pipeline (important, because "motion trails" needs this):
//
//   1. Every frame we render `this.scene` into a PERSISTENT render target
//      (`this.sceneRT`). We never clear sceneRT automatically; instead, a
//      full-screen semi-transparent quad ("trailMesh") is drawn first each
//      frame and alpha-blends with the previous frame's content already
//      sitting in sceneRT. That gives us real motion-trail feedback.
//
//      trails = 0   → trailMesh is fully opaque → full clear every frame.
//      trails = 1   → trailMesh is fully transparent → content accumulates forever.
//
//   2. A `ShaderPass` at the start of the composer's chain samples
//      sceneRT.texture into the composer's inputBuffer. From there, the
//      EffectPass applies bloom + chromatic aberration + scanlines + grain
//      + vignette and writes to screen.
//
// Why not just use postprocessing's RenderPass with clear=false?
//   Because the composer ping-pongs its input/output buffers across frames,
//   so "don't clear" means "accumulate over the POST-PROCESSED previous frame",
//   which creates a runaway bloom-feedback loop. A persistent render target
//   that we control directly is the correct solution.

import * as THREE from 'three';
import { MosaicOverlay } from './td/mosaic-overlay.js';
import {
  EffectComposer,
  EffectPass,
  ShaderPass,
  BloomEffect,
  ChromaticAberrationEffect,
  NoiseEffect,
  VignetteEffect,
  ScanlineEffect,
  BlendFunction,
  KernelSize
} from 'postprocessing';

// Passthrough shaders used by the "blit sceneRT into composer input" pass.
const BLIT_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;
const BLIT_FRAG = /* glsl */ `
  uniform sampler2D tScene;
  varying vec2 vUv;
  void main() {
    gl_FragColor = texture2D(tScene, vUv);
  }
`;

export class Engine {
  constructor(canvas) {
    this.canvas = canvas;

    // --- renderer ---
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
      alpha: false,
      depth: false,
      stencil: false,
      precision: 'mediump',
      premultipliedAlpha: false
    });
    this.renderer.setPixelRatio(1);
    this.renderer.autoClear = false;  // sceneRT is persisted; we never auto-clear it

    // --- scene & camera ---
    this.scene  = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);

    // --- persistent scene render target (drives motion trails) ---
    this.sceneRT = new THREE.WebGLRenderTarget(2, 2, {
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter
    });
    this.sceneRT.texture.generateMipmaps = false;

    // --- state ---
    this.width  = 1920;
    this.height = 1080;
    this.time   = 0;
    this.mediaTime = null;
    this.dt     = 0;
    this._lastTs = 0;
    this._fpsSamples = [];
    this.running = true;
    this.externalFrameControl = false;
    this.fps = 60;

    this.currentGenerator = null;
    this.timeline = {
      bpm: 60,
      beats: 8,
      seed: 1,
      seamless: true,
    };

    this.post = {
      trails:      0.15,
      bloom:       1.0,
      bloomThresh: 0.4,
      chromatic:   0.002,
      grain:       0.08,
      vignette:    0.35,
      scanlines:   0.05
    };

    this._buildComposer();
    this._buildTrailMesh();
    this._applyPostSettings();
    this._updateBloomResolution();

    // Mosaic overlay — async init (loads glyph PNGs); enabled toggle on params.
    this.mosaic = new MosaicOverlay();
    this.mosaic.init().catch(err => console.warn('Mosaic init failed', err));
  }

  // ---------------------------------------------------------------
  // Composer: ShaderPass (samples sceneRT) → EffectPass (bloom etc.)
  // ---------------------------------------------------------------
  _buildComposer() {
    this.composer = new EffectComposer(this.renderer, {
      frameBufferType: THREE.UnsignedByteType,
      multisampling: 0,
      depthBuffer: false,
      stencilBuffer: false
    });

    // ShaderPass with no `input` arg means the composer's inputBuffer is NOT
    // auto-wired. We explicitly sample our sceneRT.texture via tScene.
    this._blitMaterial = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this.sceneRT.texture } },
      vertexShader: BLIT_VERT,
      fragmentShader: BLIT_FRAG,
      depthTest: false,
      depthWrite: false
    });
    this.blitPass = new ShaderPass(this._blitMaterial);
    this.composer.addPass(this.blitPass);

    // Effects
    this.bloomEffect = new BloomEffect({
      intensity: 1.0,
      luminanceThreshold: 0.4,
      luminanceSmoothing: 0.3,
      mipmapBlur: true,
      kernelSize: KernelSize.MEDIUM
    });
    this.chromaticEffect = new ChromaticAberrationEffect({
      offset: new THREE.Vector2(0.002, 0.0),
      radialModulation: true,
      modulationOffset: 0.15
    });
    this.scanlineEffect = new ScanlineEffect({
      density: 1.25,
      blendFunction: BlendFunction.OVERLAY
    });
    this.scanlineEffect.blendMode.opacity.value = 0.05;

    this.noiseEffect = new NoiseEffect({
      blendFunction: BlendFunction.OVERLAY,
      premultiply: true
    });
    this.noiseEffect.blendMode.opacity.value = 0.08;

    this.vignetteEffect = new VignetteEffect({
      darkness: 0.35,
      offset: 0.5
    });

    this.fxPass = new EffectPass(
      this.camera,
      this.bloomEffect,
      this.chromaticEffect,
      this.scanlineEffect,
      this.noiseEffect,
      this.vignetteEffect
    );
    this.composer.addPass(this.fxPass);
  }

  // ---------------------------------------------------------------
  // Trail mesh — sits in this.scene at renderOrder -9999 so it draws
  // first every frame, alpha-blending with the previous frame content.
  // ---------------------------------------------------------------
  _buildTrailMesh() {
    this._trailMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color('#080611'),
        transparent: true,
        opacity: 1 - this.post.trails,
        depthWrite: false,
        depthTest: false
      })
    );
    this._trailMesh.renderOrder = -9999;
    this._trailMesh.frustumCulled = false;
    this._updateTrailMeshScale();
    this.scene.add(this._trailMesh);
  }

  _updateTrailMeshScale() {
    if (!this._trailMesh) return;
    const aspect = this.width / this.height;
    this._trailMesh.scale.set(aspect, 1, 1);
  }

  _applyPostSettings() {
    const p = this.post;
    this._trailMesh.material.opacity = Math.max(0.0, Math.min(1.0, 1 - p.trails));
    this.bloomEffect.intensity = p.bloom;
    this.bloomEffect.luminanceMaterial.threshold = p.bloomThresh;
    this.chromaticEffect.offset.x = p.chromatic;
    this.chromaticEffect.offset.y = 0;
    this.noiseEffect.blendMode.opacity.value = p.grain;
    this.vignetteEffect.uniforms.get('darkness').value = p.vignette;
    this.scanlineEffect.blendMode.opacity.value = p.scanlines;
  }

  _updateBloomResolution() {
    const minBloomHeight = 360;
    const bloomScale = Math.max(0.35, Math.min(1, minBloomHeight / Math.max(1, this.height)));

    if (this.bloomEffect?.resolution) {
      this.bloomEffect.resolution.scale = bloomScale;
    }

    if (this.bloomEffect?.mipmapBlurPass?.resolution) {
      this.bloomEffect.mipmapBlurPass.resolution.scale = bloomScale;
    }
  }

  setPostParam(key, value) {
    if (key in this.post) {
      this.post[key] = value;
      this._applyPostSettings();
    }
  }

  setBackground(hex) {
    this._trailMesh.material.color.set(hex);
  }

  getLoopDuration() {
    return (this.timeline.beats * 60) / Math.max(1, this.timeline.bpm);
  }

  getLoopInfo(time = this.time) {
    const duration = this.getLoopDuration();
    const phase = duration > 0 ? ((time % duration) + duration) % duration / duration : 0;
    const beatFloat = phase * this.timeline.beats;
    return {
      duration,
      phase,
      beatPhase: beatFloat - Math.floor(beatFloat),
      beatIndex: Math.floor(beatFloat),
      beats: this.timeline.beats,
      bpm: this.timeline.bpm,
      seed: this.timeline.seed,
      seamless: this.timeline.seamless,
    };
  }

  setTimeline(partial) {
    const prevSeed = this.timeline.seed;
    Object.assign(this.timeline, partial);
    if (this.currentGenerator?.setSeed) {
      this.currentGenerator.setSeed(this.timeline.seed);
    }
    if (this.currentGenerator?.reseed && this.timeline.seed !== prevSeed) {
      this.currentGenerator.reseed();
    }
  }

  restartLoop() {
    this.time = 0;
    this.mediaTime = null;
    this.dt = 0;
    this._lastTs = 0;
    this._fpsSamples = [];

    if (this.currentGenerator?.setSeed) {
      this.currentGenerator.setSeed(this.timeline.seed);
    }
    if (this.currentGenerator?.reseed) {
      this.currentGenerator.reseed();
    }

    this.renderer.setRenderTarget(this.sceneRT);
    this.renderer.setClearColor(0x000000, 1);
    this.renderer.clear(true, true, true);
    this.renderer.setRenderTarget(null);
  }

  // ---------------------------------------------------------------
  // Render sizing — decoupled from DOM; sets the TRUE pixel dimensions
  // the scene is rendered at.
  // ---------------------------------------------------------------
  setRenderSize(width, height) {
    this.width  = Math.max(2, Math.round(width));
    this.height = Math.max(2, Math.round(height));

    this.renderer.setSize(this.width, this.height, false);
    this.renderer.setViewport(0, 0, this.width, this.height);
    this.renderer.setScissor(0, 0, this.width, this.height);
    this.renderer.setScissorTest(false);
    this.composer.setSize(this.width, this.height);
    this.mosaic?.setSize(this.width, this.height);
    this.sceneRT.setSize(this.width, this.height);
    this.sceneRT.viewport.set(0, 0, this.width, this.height);
    this.sceneRT.scissor.set(0, 0, this.width, this.height);
    this.sceneRT.scissorTest = false;

    const aspect = this.width / this.height;
    this.camera.left   = -aspect;
    this.camera.right  =  aspect;
    this.camera.top    =  1;
    this.camera.bottom = -1;
    this.camera.updateProjectionMatrix();
    this._updateTrailMeshScale();
    this._updateBloomResolution();

    if (this.currentGenerator?.onResize) {
      this.currentGenerator.onResize(this.width, this.height);
    }
  }

  // ---------------------------------------------------------------
  // Generator slot
  // ---------------------------------------------------------------
  setGenerator(generator) {
    if (this.currentGenerator) {
      this.currentGenerator.onExit?.(this.scene);
    }
    this.currentGenerator = generator;
    if (generator) {
      generator.setSeed?.(this.timeline.seed);
      generator.onEnter(this.scene, this);
      if (generator.onResize) generator.onResize(this.width, this.height);
      // Flush the persisted target so we don't see the previous generator's
      // trails bleed into this one.
      this.renderer.setRenderTarget(this.sceneRT);
      this.renderer.setClearColor(0x000000, 1);
      this.renderer.clear(true, true, true);
      this.renderer.setRenderTarget(null);
    }
  }

  pxToNdc(px, py) {
    const aspect = this.width / this.height;
    const x = (px / this.width) * 2 * aspect - aspect;
    const y = 1 - (py / this.height) * 2;
    return [x, y];
  }

  // ---------------------------------------------------------------
  // Main loop — call once per requestAnimationFrame.
  // ---------------------------------------------------------------
  tick() {
    if (this.externalFrameControl) return;

    const now = performance.now();
    this.dt = this._lastTs ? Math.min(0.1, (now - this._lastTs) / 1000) : 0.016;
    this._lastTs = now;
    this._renderFrame(this.dt);
  }

  renderFixedFrame(dt) {
    this.dt = dt;
    this._renderFrame(dt);
  }

  _renderFrame(dt) {
    if (this.running) {
      this.time += dt;
      if (this.mediaTime !== null) this.mediaTime += dt;
    }

    // fps smoothing
    this._fpsSamples.push(dt);
    if (this._fpsSamples.length > 60) this._fpsSamples.shift();
    const avg = this._fpsSamples.reduce((a, b) => a + b, 0) / this._fpsSamples.length;
    this.fps = 1 / Math.max(0.0001, avg);

    if (this.running && this.currentGenerator) {
      this.currentGenerator.update(dt, this.time, this);
    }

    // 1. Render scene into persistent sceneRT (trail mesh fades previous frame).
    this.renderer.setRenderTarget(this.sceneRT);
    this.renderer.setViewport(0, 0, this.width, this.height);
    this.renderer.setScissor(0, 0, this.width, this.height);
    this.renderer.setScissorTest(false);
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(null);

    // 1.5 Mosaic overlay — re-renders sceneRT as a letter grid into mosaicRT,
    // then redirects the composer's blit to read from there.
    let blitSource = this.sceneRT.texture;
    if (this.mosaic?.enabled && this.mosaic.ready) {
      blitSource = this.mosaic.render(this.renderer, this.sceneRT.texture);
    }
    if (this._blitMaterial.uniforms.tScene.value !== blitSource) {
      this._blitMaterial.uniforms.tScene.value = blitSource;
    }

    // 2. Composer blits (mosaic or sceneRT) to inputBuffer then runs bloom / chromatic / etc.
    this.renderer.setViewport(0, 0, this.width, this.height);
    this.renderer.setScissor(0, 0, this.width, this.height);
    this.renderer.setScissorTest(false);
    this.composer.render(dt);
  }

  dispose() {
    this.composer.dispose();
    this.renderer.dispose();
    this.sceneRT.dispose();
  }
}
